import { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Services and Interfaces
import { IRecipe } from "@/interfaces/recipe/recipe";

// Styles
import { Copy, TrashCan } from "@carbon/icons-react";
import RecipePlaceHolder from "@/assets/recipes/recipe_placeholder.png";

// Components
import Tag from "@/components/tag";
import { Box } from "@mui/material";
import CopyRecipeModal from "@/pages/library/recipes/copy_recipe";
import RecipePopover from "@/pages/library/recipes/recipe_popover";
import DeleteRecipeModal from "@/pages/library/recipes/delete_recipe";
import Details from "@/components/recipe_builder/recipe_card/details";
import Actions from "@/components/recipe_builder/recipe_card/actions";
import MacroTags from "@/components/recipe_builder/recipe_card/macro_tags";
import SelectBox from "@/components/recipe_builder/recipe_card/select_box";
import LoadingRecipe from "@/components/nutrition_builder/recipes/loading_recipe_card";


interface _RecipeCardProps {
    recipe: IRecipe;
    isLoading?: boolean;
    isDisabled?: boolean;
    isSelected?: boolean;
    onSelect?: (val: boolean) => void;
}

const RecipeCard: FC<_RecipeCardProps> = ({ 
    recipe,
    isLoading = false,
    isDisabled = false,
    isSelected = false,
    onSelect,
}) => {

    const navigate = useNavigate();
    const cardRef = useRef<HTMLDivElement>(null);
    const hoverTimeoutRef = useRef<NodeJS.Timeout|null>(null);
    const [modalOpen, setModalOpen] = useState<string|null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement|null>(null);
    const open = Boolean(anchorEl);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        if (isLoading || (event.target as HTMLElement).closest(".PreventPopover")) return;
        hoverTimeoutRef.current = setTimeout(() => {
            setAnchorEl(cardRef.current);
        }, 700);
    };
  
    const handlePopoverClose = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setAnchorEl(null);
    };

    const handleCloseModal = () => {
        setModalOpen(null);
        handlePopoverClose();
    }

    const handleOpenRecipe = () => {
        if (isDisabled) return;
        onSelect ? onSelect(!isSelected) : navigate(`/library/recipes/${recipe.id}`);
    }

    if (isLoading) {
        return <LoadingRecipe />;
    }

    return (
        <>
            <Box
                ref={cardRef}
                onMouseEnter={handlePopoverOpen} 
                onMouseLeave={handlePopoverClose}
                onClick={handleOpenRecipe}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    textAlign: 'left',
                    borderRadius: "10px",
                    border: "solid 1px var(--border-subtle-01)",
                    boxShadow: "6px 6px 12px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    cursor: 'pointer',
                    "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "10px 10px 15px rgba(0,0,0,0.2)",
                        filter: "brightness(0.9)",
                        '& .Recipe__actions': {
                            opacity: 1
                        }
                    },
                }}
                >
                {/* HEADER */}
                <Box 
                    position='relative' 
                    width='100%'
                    minHeight="120px"
                    sx={{
                        aspectRatio: 16/9,
                        background: `url(${recipe.image ?? RecipePlaceHolder})`,
                        // background: `linear-gradient(180deg, #FFFFFF00 19%, #1D212D 100%), url(${recipe.image ?? RecipePlaceHolder})`,
                        backgroundSize: 'cover', backgroundPosition: 'center',
                    }}>

                    {/* Draft Tag */}
                    {recipe.draft && <Tag
                        colour="blue"
                        outlined
                        sx={{position: 'absolute', top: '8px', left: '8px'}}
                        label="Draft"
                        />}

                    {/* Actions */}
                    {!onSelect && <Actions
                        action1={{
                            icon: <TrashCan size={16} />,
                            onClick: () => setModalOpen('delete')
                        }}
                        action2={{  
                            icon: <Copy size={16} />,
                            onClick: () => setModalOpen('copy')
                        }}
                        onMouseEnter={handlePopoverClose}
                        onMouseLeave={handlePopoverOpen}
                        />}

                    {modalOpen === 'delete' && <DeleteRecipeModal
                        open={modalOpen === 'delete'}
                        onClose={handleCloseModal}
                        recipe_id={recipe.id}
                        name={recipe.title}
                        />}
                    {modalOpen === 'copy' && <CopyRecipeModal
                        open={modalOpen === 'copy'}
                        onClose={handleCloseModal}
                        recipe_id={recipe.id}
                        name={recipe.title}
                        />}

                    {/* Select Box */}
                    {!!onSelect && <SelectBox
                        isSelected={isSelected}
                        isDisabled={isDisabled}
                        onMouseEnter={handlePopoverClose}
                        onMouseLeave={handlePopoverOpen}
                        />}

                    {/* Tags */}
                    <MacroTags
                        servings={recipe.servings}
                        calories={recipe.calories}
                        protein={recipe.protein}
                        carbs={recipe.carbs}
                        fat={recipe.fat}
                        isLoading={isLoading}
                        />
                </Box>

                {/* Details */}
                <Details 
                    title={recipe.title}
                    prepTime={recipe.prep_time}
                    cookTime={recipe.cook_time}
                    published={recipe.published}
                    isLoading={isLoading}
                    />

            </Box>

            <RecipePopover
                open={open && !modalOpen}
                onClose={handlePopoverClose}
                anchorEl={anchorEl}
                recipe={recipe}
                />
        </>
    );
};

export default RecipeCard;
